import { useState } from 'react';
import { SYMBOLE, bannerMeta, hatEmoji, type Antworten, type Daten, type Feld } from '../../lib/mailBuilder/vorlagen';
import { TextEditor } from '../../components/TextEditor/TextEditor';
import { BannerEditor } from '../../components/BannerEditor/BannerEditor';

type Props = {
  feld: Feld;
  daten: Daten;
  antworten: Antworten;
  onChange: (wert: string) => void;
  /** Für Felder, die mehr als einen Wert schreiben (Banner: Bild und Zuschnitt). */
  setzen: (id: string, wert: string) => void;
  /** Nach dem ersten „Weiter" fehlende Pflichtfelder markieren. */
  zeigeFehler: boolean;
};

function istPflicht(f: Feld, d: Daten, a: Antworten): boolean {
  return typeof f.pflicht === 'function' ? f.pflicht(d, a) : !!f.pflicht;
}

/** Ein Feld einer Vorlage — Beschriftung, Eingabe, Hinweis, Fehler. */
export function FeldEingabe({ feld, daten, antworten, onChange, setzen, zeigeFehler }: Props) {
  const wert = daten[feld.id] ?? '';
  const pflicht = istPflicht(feld, daten, antworten);
  const fehlt = zeigeFehler && pflicht && !wert.trim() && !(feld.typ === 'auswahl' && feld.standard);
  const id = `feld-${feld.id}`;
  const labelId = `${id}-label`;
  const hinweisId = feld.hinweis || feld.typ === 'textarea' || feld.typ === 'zeilen' ? `${id}-hinweis` : undefined;
  const klasse = `${fehlt ? ' input-error' : ''}`;
  const aria = { 'aria-invalid': fehlt || undefined, 'aria-describedby': hinweisId, 'aria-required': pflicht || undefined };

  let eingabe: React.ReactNode;
  switch (feld.typ) {
    case 'textarea':
      eingabe = (
        <TextEditor id={id} value={wert} onChange={onChange} placeholder={feld.platzhalter} zeilen={5}
          invalid={fehlt} required={pflicht} labelledBy={labelId} describedBy={hinweisId} />
      );
      break;
    case 'zeilen':
      eingabe = (
        <textarea id={id} className={`form-textarea${klasse}`} value={wert} rows={4}
          placeholder={feld.platzhalter} onChange={(e) => onChange(e.target.value)} {...aria} />
      );
      break;
    case 'datum':
      eingabe = <input id={id} type="date" className={`form-input${klasse}`} value={wert} onChange={(e) => onChange(e.target.value)} {...aria} />;
      break;
    case 'uhrzeit':
      eingabe = <input id={id} type="time" className={`form-input${klasse}`} value={wert} onChange={(e) => onChange(e.target.value)} {...aria} />;
      break;
    case 'url':
      eingabe = <input id={id} type="url" inputMode="url" className={`form-input${klasse}`} value={wert} placeholder={feld.platzhalter ?? 'https://…'} onChange={(e) => onChange(e.target.value.trim())} {...aria} />;
      break;
    case 'auswahl':
      eingabe = (
        <select id={id} className={`form-select${klasse}`} value={wert || feld.standard || ''} onChange={(e) => onChange(e.target.value)} {...aria}>
          {!pflicht && !feld.optionen?.some((o) => o.wert === '') && <option value="">— bitte wählen —</option>}
          {feld.optionen?.map((o) => <option key={o.wert} value={o.wert}>{o.label}</option>)}
        </select>
      );
      break;
    case 'banner':
      eingabe = (
        <BannerEditor
          id={id}
          bild={wert}
          meta={bannerMeta(daten)}
          onChange={(bild, meta) => { setzen('bild_meta', meta ? JSON.stringify(meta) : ''); setzen(feld.id, bild); }}
        />
      );
      break;
    case 'symbol':
      eingabe = <SymbolFeld id={id} wert={wert} standard={feld.standard ?? ''} onChange={onChange} describedBy={hinweisId} />;
      break;
    default:
      eingabe = (
        <input id={id} type="text" className={`form-input${klasse}`} value={wert} maxLength={feld.maxLaenge ? feld.maxLaenge + 20 : undefined}
          placeholder={feld.platzhalter} onChange={(e) => onChange(e.target.value)} {...aria} />
      );
  }

  const zuLang = feld.maxLaenge && wert.length > feld.maxLaenge;
  // Ein Klick auf die Beschriftung setzt den Cursor auch ins Textfeld mit Leiste.
  const fokus = feld.typ === 'textarea' ? () => document.getElementById(id)?.focus() : undefined;

  return (
    <div className="form-group">
      <label className="form-label" id={labelId} htmlFor={feld.typ === 'textarea' ? undefined : id} onClick={fokus}>
        {feld.label}{pflicht && <span className="as-pflicht" aria-hidden="true"> *</span>}
      </label>
      {eingabe}
      {hinweisId && (
        <p className="form-hint" id={hinweisId}>
          {feld.hinweis ?? (feld.typ === 'zeilen'
            ? 'Eine Angabe je Zeile.'
            : 'Mit der Leiste formatierst Du: fett, kursiv, markiert, Farbe und Größe. Eine Leerzeile beginnt einen neuen Absatz.')}
        </p>
      )}
      {feld.maxLaenge && feld.typ === 'text' && wert.length > feld.maxLaenge * 0.8 && (
        <p className={`form-hint${zuLang ? ' as-zu-lang' : ''}`}>{wert.length} / {feld.maxLaenge} Zeichen</p>
      )}
      {fehlt && <p className="field-error" role="alert">Bitte ausfüllen.</p>}
    </div>
  );
}

/** Nur Emojis (keine Buchstaben oder Ziffern), höchstens ein paar Zeichen. */
function istEmoji(s: string): boolean {
  const t = s.trim();
  return !!t && t.length <= 16 && hatEmoji(t) && !/[\p{L}\p{N}]/u.test(t.replace(/[⃣]/g, ''));
}

function SymbolFeld({ id, wert, standard, onChange, describedBy }: {
  id: string; wert: string; standard: string; onChange: (w: string) => void; describedBy?: string;
}) {
  const bekannt = wert === '' || wert === 'auto' || wert === 'kein' || SYMBOLE.some((s) => s.wert === wert);
  const [eigen, setEigen] = useState(!bekannt);
  const [eingabe, setEingabe] = useState(bekannt ? '' : wert);
  const auswahl = eigen ? 'eigen' : wert === 'auto' ? '' : wert;
  const ungueltig = eigen && eingabe.trim() !== '' && !istEmoji(eingabe);

  return (
    <div className="as-symbol">
      <div className="as-zeile">
        <select id={id} className="form-select" value={auswahl} aria-describedby={describedBy}
          onChange={(e) => {
            const w = e.target.value;
            if (w === 'eigen') { setEigen(true); if (istEmoji(eingabe)) onChange(eingabe.trim()); return; }
            setEigen(false);
            onChange(w);
          }}>
          <option value="">Passend zur Vorlage{standard ? ` (${standard})` : ''}</option>
          {SYMBOLE.map((s) => <option key={s.wert} value={s.wert}>{s.wert} {s.label}</option>)}
          <option value="eigen">Eigenes Emoji …</option>
          <option value="kein">Kein Symbol</option>
        </select>
        {eigen && (
          <input
            className={`form-input as-symbol-eingabe${ungueltig ? ' input-error' : ''}`}
            value={eingabe}
            maxLength={16}
            placeholder="z. B. 🥇"
            aria-label="Eigenes Emoji"
            aria-invalid={ungueltig || undefined}
            onChange={(e) => {
              setEingabe(e.target.value);
              if (istEmoji(e.target.value)) onChange(e.target.value.trim());
            }}
          />
        )}
      </div>
      {eigen && (
        <p className={ungueltig ? 'field-error' : 'form-hint'} role={ungueltig ? 'alert' : undefined}>
          {ungueltig
            ? 'Bitte nur ein oder zwei Emojis, ohne Buchstaben.'
            : 'Emoji einfügen: am Computer mit Windows-Taste + Punkt bzw. Strg + Cmd + Leertaste am Mac, am Handy über die Emoji-Tastatur.'}
        </p>
      )}
    </div>
  );
}
